<template>
  <div class="document-card" :class="documentClasses" :id="'card-' + tenantId + '-' + id">
    <div class="document-card__topic">
      <template v-if="localAccepted || (localFilled && !localAccepted) || localNotRequired">
        <h3 class="document-card__title">
          <span v-if='alreadyAddedDocuments && alreadyAddedDocuments.length' class="mr-1">
            <theme-checkboxes name="check_all" @changed='toggleAllFiles' v-if="!disableRemove && !removeBlockedMessage"></theme-checkboxes>
          </span>
          {{ title }}
          <i
            v-if="localAccepted"
            class="icon-check ml-auto text-[#77b866] text-[15px]"
            role="img"
            :aria-label="locales && locales.common && locales.common.approved"
          ></i>
        </h3>
      </template>
      <template v-if="!localAccepted && !localFilled && !localNotRequired">
        <h3 class="document-card__title">
          {{ title }}
          <i v-if="showReason" class="icon-warning ml-auto text-[#FF695B] text-[20px]"></i>
          <span v-if="showReason" class='text-[#FF695B] text-[14px] font-bold hidden md:inline'>{{ locales && locales.common && locales.common.issue_with_document }}</span>
        </h3>
      </template>
    </div>
    <div class="document-card__inner">
      <strong v-if="localAccepted" class="document-card__subheading">{{subtitle}}</strong>
      <span v-if="!localAccepted && !localFilled" class="document-card__subheading">
        {{ notificationPrefix }}
        <strong>
          <span v-if="localTypeMonths">{{ numberOfDocuments }}</span>
          {{ notification }}
        </strong>
      </span>
      <strong v-if="localFilled && !localAccepted" class="document-card__subheading">{{ cardFilledNotification }}</strong>

      <!-- default card template -->
      <div class="document-card__block" v-if="localTypeDefault">
        <!-- List of images array -->
        <div v-if='alreadyAddedDocuments && alreadyAddedDocuments.length' class="document-card__row">
          <ol class="document-card__image-list">
            <li v-for="(documentFile, index) in alreadyAddedDocuments" :key="documentFile.id">
              <template v-if="!documentFile.disable_remove && !disableRemove">
                <theme-checkboxes
                  v-if="!removeBlockedMessage"
                  :setForm="true"
                  form="removeForm"
                  name="remove[]"
                  :options="{ [documentFile.id]: documentFile.imageName }"
                  @changed='emitChange'
                ></theme-checkboxes>
                <span v-else class="document-card__image-name">{{ documentFile.imageName }}</span>
                <button
                  type="button"
                  class="document-card__delete-document"
                  @click="deleteAddedDocument(documentFile.id, index)"
                >
                  <i class='icon-delete'/>
                  {{ locales && locales.documents && locales.documents.delete_document}}
                </button>
              </template>
              <span v-else class="document-card__image-name">
                {{ documentFile.imageName }}
              </span>
            </li>
          </ol>
        </div>

        <div class="document-card__row">
          <!-- XHR upload mode -->
          <template v-if="uploadUrl && !readOnly">
            <div v-show="!checkboxState && showUploadRail">
              <theme-file
                :key="'upload-default-' + uploadKeyCounter"
                inputClass="document-card__input-file"
                :accept="uploadAccept"
                :name="'file[' + tenantId + '][' + id + '][]'"
                :id="inputFileId + '-upload'"
                @change="handleUploadFileChange($event, -1)"
                multiple
              />
            </div>
            <!-- Upload queue: one row per file in flight/queued/processing/failed for this card (no fixed count) -->
            <div v-for="upload in uploadsForSlot(-1)" :key="upload.id" class="document-card__progress-zone">
              <div v-if="upload.status === 'uploading'" class="document-card__progress">
                <div
                  class="document-card__progress-bar"
                  :class="{'document-card__progress-bar--success': upload.progress >= 100}"
                  :style="{width: upload.progress + '%'}"
                ></div>
                <div class="document-card__progress-text">{{ upload.name }} — {{ upload.progress }}%</div>
              </div>
              <div v-else-if="upload.status === 'queued' || upload.status === 'processing'" class="document-card__progress-status" role="status" aria-live="polite">
                <div class="document-card__progress-text">
                  {{ upload.name }} — {{ (locales.documents && locales.documents['upload_status_' + upload.status]) || upload.status }}
                </div>
                <small>{{ locales.documents && locales.documents.upload_processing_continues_notice }}</small>
              </div>
              <div v-else-if="upload.status === 'failed'" class="document-card__progress-error" role="alert">
                {{ upload.name }} — {{ upload.error }}
                <button
                  v-if="upload.retryable"
                  type="button"
                  class="theme-btn theme-btn--small theme-btn--tertiary"
                  @click="retryUpload(upload)"
                >{{ locales.documents && locales.documents.upload_retry_action }}</button>
              </div>
            </div>
            <small v-show="!checkboxState && showUploadRail" class="document-card__file-hint">PDF, JPG, PNG</small>
          </template>
          <!-- Traditional form upload mode -->
          <template v-else-if="!readOnly">
            <ul v-show="!checkboxState" class="document-card__image-list js-inputs-parent">
              <li v-for="(input, index) in inputsTypeFile">
                <theme-file
                  inputClass="document-card__input-file"
                  :accept="uploadAccept"
                  :name="'file[' + tenantId + '][' + id + '][]'"
                  :id="inputFileId + '-' + index"
                  @change="fileInputChanged($event, index)"
                />
              </li>
            </ul>
            <small v-show="!checkboxState" class="document-card__file-hint">PDF, JPG, PNG — max. 20 MB</small>
          </template>
          <div class="document-card__image-list-bottom">
            <!-- Variant A+: "+ Extra document" when accepted with uploadUrl -->
            <button
              v-if="uploadUrl && localAccepted && !extraSlotOpen && !checkboxState && !readOnly"
              class="theme-btn theme-btn--small theme-btn--tertiary"
              type="button"
              @click="openExtraUploadSlot"
            >
              <i class='icon-plus'/>
              {{ locales.common && locales.common.new_document }}
            </button>
            <button
              v-show="!checkboxState"
              v-if="!uploadUrl && !readOnly"
              class="theme-btn theme-btn--small theme-btn--tertiary"
              type="button"
              @click="addInputTypeFile"
            >
              <i class='icon-plus'/>
              {{ locales.common && locales.common.new_document }}
            </button>
            <!-- do not have document string -->
            <div v-if="showReason && !readOnly" class="document-card__flag-checkbox-holder">
              <input
                type="hidden"
                value=""
                :name="'reason[' + tenantId + '][' + id + ']'"
              />
              <label
                :for="inputCheckboxId"
                class="document-card__flag-checkbox-label"
                v-show="!isDocumentsAdded"
              >
                <input
                  type="checkbox"
                  class="document-card__flag-checkbox"
                  :name="inputCheckboxId"
                  :id="inputCheckboxId"
                  v-model="checkboxState"
                  @change="cardChanged"
                  :disabled="isDocumentsAdded"
                />
                <i class='document-card__flag-checkbox-icon'/>
                <span>{{ checkboxText }}</span>
              </label>
            </div>
          </div>
        </div>
        <!-- hidden textarea -->
        <div class="document-card__row" v-if="checkboxState">
          <textarea
            class="document-card__textarea"
            :id="textareaId"
            :name="'reason[' + tenantId + '][' + id + ']'"
            :placeholder="textareaPlaceholder"
            v-model="localTextareaData"
            :readonly="readOnly"
            @keyup="cardChanged"
          ></textarea>
        </div>
        <div class="document-card__buttons-group" v-if="!readOnly" v-show="!uploadUrl || checkboxState">
          <button
            v-if="reasonUrl && uploadUrl"
            type="button"
            :value="buttonValue"
            :disabled="!reasonIsDirty || loading"
            :class="['theme-btn theme-btn--small theme-btn--primary', (!reasonIsDirty || !reasonIsComplete || loading) && 'theme-btn--primary-disabled']"
            @click="_saveReason($event)"
          >{{ buttonValue }}</button>
          <input
            v-else
            type="submit"
            name="submit"
            :value="buttonValue"
            :disabled="!localFilled || !isDirty"
            :class="['theme-btn theme-btn--small theme-btn--primary', (!localFilled || !isDirty) && 'theme-btn--primary-disabled', loading && 'theme-btn--primary-disabled']"
            @click='loading = true'
          />
          <small v-if="(!localFilled || !isDirty) && !localAccepted && !uploadUrl" class="document-card__submit-hint">
            {{ locales && locales.common && locales.common.choose_file }}
          </small>
        </div>
        <div class="document-card__cta-group" v-if="(showEdrButton || showGuaranteeButton) && !readOnly">
          <button
            type="submit"
            @click.prevent="requestEdrLink"
            class="theme-btn theme-btn--small theme-btn--theme"
            :class="{ 'theme-btn--theme-disabled': edrRequestInFlight }"
            :disabled="edrRequestInFlight"
            :formaction="edrAction"
            form="edr"
            v-if="showEdrButton"
          >
            {{ locales.users && locales.users.request_edr_check }}
          </button>
          <button
            type="submit"
            @click.prevent="requestGuarantee"
            class="theme-btn theme-btn--small theme-btn--theme"
            :formaction="guaranteeAction"
            form="guarantee"
            v-if="showGuaranteeButton"
          >
            {{ locales.documents && locales.documents.request_guarantee }}
          </button>
        </div>
      </div>

      <!-- 3 month card template -->
      <div class="document-card__block" v-if="localTypeMonths">
        <div
          class="document-card__row document-card__row--border"
          v-for="(month, parentIndex) in localMonths"
          :key="month.id"
        >
          <div class="document-card__row-title">{{ month.date }}</div>
          <!-- List of images array -->
          <div v-if='month.documents && month.documents.length' class="document-card__row">
            <ol class="document-card__image-list">
              <li v-for="(documentFile, index) in month.documents" :key="documentFile.id">
                <template v-if="!documentFile.disable_remove && !disableRemove">
                  <theme-checkboxes
                    v-if="!removeBlockedMessage"
                    :setForm="true"
                    form="removeForm"
                    name="remove[]"
                    :options="{ [documentFile.id]: documentFile.imageName }"
                    @changed='emitChange'
                  ></theme-checkboxes>
                  <span v-else class="document-card__image-name">{{ documentFile.imageName }}</span>
                  <button
                    type="button"
                    class="document-card__delete-document"
                    @click="deleteAddedDocument(documentFile.id, parentIndex, index)"
                  >
                    <i class='icon-delete'/>
                    {{ locales && locales.documents && locales.documents.delete_document}}
                  </button>
                </template>
                <span v-else class="document-card__image-name">
                  {{ documentFile.imageName }}
                </span>
              </li>
            </ol>
          </div>
          <!-- choose input -->
          <div class="document-card__row">
            <!-- XHR upload mode -->
            <template v-if="uploadUrl && !readOnly">
              <div v-show="!month.checkboxValue && showMonthUploadRail(parentIndex)">
                <theme-file
                  :key="'upload-month-' + parentIndex + '-' + uploadKeyCounter"
                  inputClass="document-card__input-file"
                  :accept="uploadAccept"
                  :name="'month_file[' + tenantId + '][' + id + '][' + month.index + '][]'"
                  :id="inputFileId + '-' + month.id + '-upload'"
                  @change="handleUploadFileChange($event, parentIndex)"
                  multiple
                />
              </div>
              <!-- Upload queue for this month slot: one row per file in flight/queued/processing/failed -->
              <div v-for="upload in uploadsForSlot(parentIndex)" :key="upload.id" class="document-card__progress-zone">
                <div v-if="upload.status === 'uploading'" class="document-card__progress">
                  <div
                    class="document-card__progress-bar"
                    :class="{'document-card__progress-bar--success': upload.progress >= 100}"
                    :style="{width: upload.progress + '%'}"
                  ></div>
                  <div class="document-card__progress-text">{{ upload.name }} — {{ upload.progress }}%</div>
                </div>
                <div v-else-if="upload.status === 'queued' || upload.status === 'processing'" class="document-card__progress-status" role="status" aria-live="polite">
                  <div class="document-card__progress-text">
                    {{ upload.name }} — {{ (locales.documents && locales.documents['upload_status_' + upload.status]) || upload.status }}
                  </div>
                  <small>{{ locales.documents && locales.documents.upload_processing_continues_notice }}</small>
                </div>
                <div v-else-if="upload.status === 'failed'" class="document-card__progress-error" role="alert">
                  {{ upload.name }} — {{ upload.error }}
                  <button
                    v-if="upload.retryable"
                    type="button"
                    class="theme-btn theme-btn--small theme-btn--tertiary"
                    @click="retryUpload(upload)"
                  >{{ locales.documents && locales.documents.upload_retry_action }}</button>
                </div>
              </div>
            </template>
            <!-- Traditional form upload mode -->
            <template v-else-if="!readOnly">
              <ul v-show="!month.checkboxValue" class="document-card__image-list js-inputs-parent">
                <li v-for="(input, index) in monthsInputsTypeFile[parentIndex]">
                  <theme-file
                    inputClass="document-card__input-file"
                    :accept="uploadAccept"
                    :name="'month_file[' + tenantId + '][' + id + '][' + month.index + '][]'"
                    :id="inputFileId + '-' + month.id + '-' + index"
                    @change="fileInputChanged($event, parentIndex)"
                  />
                </li>
              </ul>
            </template>
            <div class="document-card__image-list-bottom">
              <!-- Variant A+: "+ Extra document" when month has docs -->
              <button
                v-if="uploadUrl && month.documents.length && !monthsExtraSlotOpen[parentIndex] && !month.checkboxValue && !readOnly"
                class="theme-btn theme-btn--small theme-btn--tertiary"
                type="button"
                @click="openMonthExtraUploadSlot(parentIndex)"
              >
                <i class='icon-plus'/>
                {{ locales.common && locales.common.new_document }}
              </button>
              <button
                v-show="!month.checkboxValue"
                v-if="!uploadUrl && !readOnly"
                class="theme-btn theme-btn--small theme-btn--tertiary"
                type="button"
                @click="addInputTypeFile(parentIndex)"
              >
                <i class='icon-plus'/>
                {{ locales.common && locales.common.new_document }}
              </button>
              <div v-if="showReason && !readOnly" class="document-card__flag-checkbox-holder">
                <input
                  type="hidden"
                  value=""
                  :name="'month_reason[' + tenantId + '][' + id + '][' + month.index + ']'"
                />
                <label
                  :for="inputCheckboxId + '-' + parentIndex"
                  class="document-card__flag-checkbox-label"
                  v-show="!month.documents.length"
                >
                  <input
                    type="checkbox"
                    class="document-card__flag-checkbox"
                    :name="inputCheckboxId + '-' + parentIndex"
                    :id="inputCheckboxId + '-' + parentIndex"
                    v-model="month.checkboxValue"
                    @change="cardChanged"
                    :disabled="!!month.documents.length"
                  />
                  <i class='document-card__flag-checkbox-icon'/>
                  <span>{{ checkboxText }}</span>
                </label>
              </div>
            </div>
          </div>
          <!-- hidden textarea -->
          <div class="document-card__row" v-if="month.checkboxValue">
            <textarea
              class="document-card__textarea"
              :id="textareaId + '-' + parentIndex"
              :name="'month_reason[' + tenantId + '][' + id + '][' + month.index + ']'"
              :placeholder="textareaPlaceholder"
              v-model="month.textareaData"
              :readonly="readOnly"
              @keyup="cardChanged"
            ></textarea>
          </div>
        </div>
        <div class="document-card__buttons-group" v-if="!readOnly" v-show="!uploadUrl || numberOfMonthsWithReason > 0">
          <button
            v-if="reasonUrl && uploadUrl && showReason"
            type="button"
            :disabled="!reasonIsDirty || loading"
            :class="['theme-btn theme-btn--small theme-btn--primary', (!reasonIsDirty || !reasonIsComplete || loading) && 'theme-btn--primary-disabled']"
            @click="_saveReason($event)"
          >{{ buttonValue }}</button>
          <input
            v-else-if="showReason"
            type="submit"
            name="submit"
            :value="buttonValue"
            :disabled="!localFilled || !isDirty"
            :class="['theme-btn theme-btn--small theme-btn--primary', (!localFilled || !isDirty) && 'theme-btn--primary-disabled', loading && 'theme-btn--primary-disabled']"
            @click='loading = true'
          />
          <small v-if="(!localFilled || !isDirty) && !localAccepted && showReason && !uploadUrl" class="document-card__submit-hint">
            {{ locales && locales.common && locales.common.choose_file }}
          </small>
        </div>
      </div>

      <!-- alternate card template -->
      <div class="document-card__block" v-if="localTypeAlternate">
        <!-- List of images array -->
        <div v-if='alreadyAddedDocuments && alreadyAddedDocuments.length' class="document-card__row document-card__row--border">
          <ol class="document-card__image-list">
            <li v-for="(documentFile, index) in alreadyAddedDocuments" :key="documentFile.id">
              <span class="document-card__image-name">
                {{ documentFile.imageName }}
              </span>
              <span
                class="document-card__delete-document"
                @click="deleteAddedDocument(documentFile.id, index)"
                v-if="!documentFile.disable_remove && !disableRemove"
                >
                <i class='icon-delete'/>
                {{ locales && locales.documents && locales.documents.delete_document}}
              </span>
            </li>
          </ol>
        </div>
        <!-- choose input -->
        <div class="document-card__row document-card__row--border">
          <!-- XHR upload mode -->
          <template v-if="uploadUrl && !readOnly">
            <div v-show="showUploadRail">
              <theme-file
                :key="'upload-alternate-' + uploadKeyCounter"
                inputClass="document-card__input-file"
                :accept="uploadAccept"
                :name="'file[' + tenantId + '][' + id + '][]'"
                :id="inputFileId + '-upload'"
                @change="handleUploadFileChange($event, -1)"
                multiple
              />
            </div>
            <!-- Upload queue: one row per file in flight/queued/processing/failed for this card (no fixed count) -->
            <div v-for="upload in uploadsForSlot(-1)" :key="upload.id" class="document-card__progress-zone">
              <div v-if="upload.status === 'uploading'" class="document-card__progress">
                <div
                  class="document-card__progress-bar"
                  :class="{'document-card__progress-bar--success': upload.progress >= 100}"
                  :style="{width: upload.progress + '%'}"
                ></div>
                <div class="document-card__progress-text">{{ upload.name }} — {{ upload.progress }}%</div>
              </div>
              <div v-else-if="upload.status === 'queued' || upload.status === 'processing'" class="document-card__progress-status" role="status" aria-live="polite">
                <div class="document-card__progress-text">
                  {{ upload.name }} — {{ (locales.documents && locales.documents['upload_status_' + upload.status]) || upload.status }}
                </div>
                <small>{{ locales.documents && locales.documents.upload_processing_continues_notice }}</small>
              </div>
              <div v-else-if="upload.status === 'failed'" class="document-card__progress-error" role="alert">
                {{ upload.name }} — {{ upload.error }}
                <button
                  v-if="upload.retryable"
                  type="button"
                  class="theme-btn theme-btn--small theme-btn--tertiary"
                  @click="retryUpload(upload)"
                >{{ locales.documents && locales.documents.upload_retry_action }}</button>
              </div>
            </div>
            <small v-show="showUploadRail" class="document-card__file-hint">PDF, JPG, PNG</small>
          </template>
          <!-- Traditional form upload mode -->
          <template v-else-if="!readOnly">
            <ul v-show="!checkboxState" class="document-card__image-list js-inputs-parent">
              <li v-for="(input, index) in inputsTypeFile">
                <theme-file
                  inputClass="document-card__input-file"
                  :accept="uploadAccept"
                  :name="'file[' + tenantId + '][' + id + '][]'"
                  :id="inputFileId + '-' + index"
                  @change="fileInputChanged"
                />
              </li>
            </ul>
            <small v-show="!checkboxState" class="document-card__file-hint">PDF, JPG, PNG — max. 20 MB</small>
          </template>
          <div class="document-card__image-list-bottom">
            <!-- Variant A+: "+ Extra document" when accepted with uploadUrl -->
            <button
              v-if="uploadUrl && localAccepted && !extraSlotOpen && !readOnly"
              class="theme-btn theme-btn--small theme-btn--tertiary"
              type="button"
              @click="openExtraUploadSlot"
            >
              <i class='icon-plus'/>
              {{ locales.common && locales.common.new_document }}
            </button>
            <button
              v-show="!checkboxState"
              v-if="!uploadUrl && !readOnly"
              class="theme-btn theme-btn--small theme-btn--tertiary"
              type="button"
              @click="addInputTypeFile"
            >
              <i class='icon-plus'/>
              {{ locales.common && locales.common.new_document }}
            </button>
            <div v-if="showReason && !readOnly" class="document-card__flag-checkbox-holder">
              <input
                type="hidden"
                value=""
                :name="'reason[' + tenantId + '][' + id + ']'"
              />
              <label
                :for="inputCheckboxId"
                class="document-card__flag-checkbox-label"
                v-show="!isDocumentsAdded || (localTypeAlternate && uploadUrl) || !isAlternateFormFilled"
                >
                <input
                  type="checkbox"
                  class="document-card__flag-checkbox"
                  :name="inputCheckboxId"
                  :id="inputCheckboxId"
                  v-model="checkboxState"
                  @change="alternateCheckboxChanged"
                  :disabled="isDocumentsAdded"
                />
                <i class='document-card__flag-checkbox-icon'/>
                <span>{{ checkboxText }}</span>
              </label>
            </div>
          </div>
          <!-- hidden textarea -->
          <div class="document-card__row" v-if="checkboxState">
            <textarea
              class="document-card__textarea"
              :id="textareaId"
              :name="'reason[' + tenantId + '][' + id + ']'"
              :placeholder="textareaPlaceholder"
              v-model="localTextareaData"
              :readonly="readOnly"
              @keyup="cardChanged"
            ></textarea>
          </div>
        </div>
        <!-- small form -->
        <div class="document-card__row" v-if="!readOnly">
          <div class="document-card__form-title">{{ localSmallFormDescription }}</div>
          <div class="document-card__form">
            <div
              v-for="row in localSmallForm"
              class="document-card__form-box"
            >
              <label
                class="document-card__textfield-label"
                :for="textFieldId + '-' + row.id"
              >
                {{ row.labelText }}
              </label>
              <input
                class="document-card__textfield"
                type="text"
                :name="row.fieldName"
                :id="textFieldId + '-' + row.id"
                @keyup="cardChanged"
                v-model="row.inputData"
              />
            </div>
          </div>
        </div>
        <div class="document-card__buttons-group" v-if="!readOnly" v-show="!uploadUrl || checkboxState">
          <button
            v-if="reasonUrl && uploadUrl && showReason"
            type="button"
            :disabled="!reasonIsDirty || loading"
            :class="['theme-btn theme-btn--small theme-btn--primary', (!reasonIsDirty || !reasonIsComplete || loading) && 'theme-btn--primary-disabled']"
            @click="_saveReason($event)"
          >{{ buttonValue }}</button>
          <input
            v-else-if="showReason"
            type="submit"
            name="submit"
            :value="buttonValue"
            :disabled="!localFilled || !isDirty"
            :class="['theme-btn theme-btn--small theme-btn--primary', (!localFilled || !isDirty) && 'theme-btn--primary-disabled', loading && 'theme-btn--primary-disabled']"
            @click='loading = true'
          />
          <small v-if="(!localFilled || !isDirty) && !localAccepted && showReason && !uploadUrl" class="document-card__submit-hint">
            {{ locales && locales.common && locales.common.choose_file }}
          </small>
        </div>
        <div class="document-card__cta-group" v-if="showSurveyButton && !readOnly">
          <button
            type="submit"
            @click.prevent="sendSurvey"
            :disabled="disabled"
            :class="['theme-btn theme-btn--small theme-btn--theme', disabled && 'theme-btn--theme-disabled']"
            :formaction="surveyAction"
            form="edr"
            v-if="showSurveyButton"
          >
              {{
                locales.documents &&
                locales.documents.send_good_tenant_survey_to_landlord
              }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import _ from "lodash";
import $ from "jquery";
import sweetalert from "sweetalert";

// Mirrors CandidateDocumentUploadController::ALLOWED_EXTENSIONS. The browser-reported File.type is
// not reliable (empty or application/octet-stream for valid PDFs on some browsers/OSes), so the
// client only screens by extension; the server re-checks the declared extension and sniffs the
// assembled file's actual content (ProcessCandidateDocumentUploadAction).
var SUPPORTED_UPLOAD_EXTENSIONS = ["pdf", "jpg", "jpeg", "gif", "png"];
var SUPPORTED_UPLOAD_MIME_TYPES = ["application/pdf", "image/jpeg", "image/gif", "image/png"];
var UPLOAD_ACCEPT = SUPPORTED_UPLOAD_EXTENSIONS
  .map(function (extension) { return "." + extension; })
  .concat(SUPPORTED_UPLOAD_MIME_TYPES)
  .join(",");

// Shown until documents.upload_error_pdf_encrypted is published (migration
// 2026_11_03_090000_add_athome212_upload_error_translations).
var PDF_ENCRYPTED_MESSAGE = "This PDF is password-protected. Remove the password or export/print an unprotected copy and upload it again.";

export default {
  data() {
    return {
      alreadyAddedDocuments: this.addedDocuments,
      inputsTypeFile: [0],
      fileNames: [],
      monthsInputsTypeFile: [[0], [0], [0]],
      checkboxState: this.checkboxValue,
      localTextareaData: this.textareaData,
      localMonths: this.months,
      localSmallFormDescription: this.smallFormDescription,
      localSmallForm: this.smallForm,
      localAccepted: this.accepted,
      localNotRequired: this.notRequired,
      isDocumentAttached: false,
      disabled: this.surveyDisabled,
      loading: false,
      isDirty: false,
      // Resumable, chunked upload queue - one entry per file the candidate has selected,
      // independent of how many files were picked at once (multi-select) or how many cards on
      // the page are uploading simultaneously. Each entry: {id, file, name, monthIndex, status
      // ('uploading'|'queued'|'processing'|'ready'|'failed'), progress (0-100, transport only),
      // error, retryable, uploadId (server uuid), attachment}. Bounded concurrency is enforced
      // globally across every VDocumentCard instance via window.app.candidateUploadInFlight -
      // see processUploadQueue().
      uploadQueue: [],
      uploadKeyCounter: 0,
      // Variant A+ progressive disclosure
      extraSlotOpen: false,
      monthsExtraSlotOpen: {},
      // Reason dirty tracking
      initialReason: this.textareaData || '',
      initialMonthReasons: [],
      initialMonthCheckboxes: [],
      // Guards the EDR request button against duplicate clicks while the request is in flight.
      edrRequestInFlight: false,
    };
  },
  computed: {
    ...mapGetters(["metadata", "locales"]),
    uploadAccept: function () {
      return UPLOAD_ACCEPT;
    },
    numberOfDocuments: function () {
      return 3 - this.numberOfMonthsFilled;
    },
    documentClasses: function () {
      return {
        "document-card_accepted": this.localAccepted,
        "document-card_filled": this.setFilledClass,
        "document-card_declined": this.setDeclinedClass,
        "document-card_can-be-confirmed":
          this.localAccepted || this.setFilledClass || this.localNotRequired,
      };
    },
    setFilledClass: function () {
      if ((this.localFilled || this.localNotRequired) && !this.localAccepted) {
        return true;
      } else {
        return false;
      }
    },
    setDeclinedClass: function () {
      if (!this.localAccepted && !this.localFilled && !this.localNotRequired) {
        return true;
      } else {
        return false;
      }
    },
    inputFileId: function () {
      return "document-" + this.id + this.tenantId;
    },
    inputCheckboxId: function () {
      return "checkbox-" + this.id + this.tenantId;
    },
    textFieldId: function () {
      return "textfield-" + this.id + this.tenantId;
    },
    textareaId: function () {
      return "textarea-" + this.id + this.tenantId;
    },
    isDocumentsAdded: function () {
      return !!this.alreadyAddedDocuments.length;
    },
    isAlternateFormFilled: function () {
      var alternateForm = [false, false, false];

      for (var i = 0; i < 3; i++) {
        if (this.localSmallForm[i].inputData.length) {
          alternateForm[i] = true;
        }
      }

      if (
        alternateForm[0] === true &&
        alternateForm[1] === true &&
        alternateForm[2] === true
      ) {
        return true;
      } else {
        return false;
      }
    },
    numberOfMonthsFilled: function () {
      var month = 0;

      for (var i = 0; i < 3; i++) {
        if (this.localMonths[i].documents.length) {
          month++;
        } else {
          if (
            this.localMonths[i].textareaData.length &&
            this.localMonths[i].checkboxValue
          ) {
            month++;
          }
        }
      }

      return month;
    },
    isMonthsFilled: function () {
      var month = [false, false, false];

      for (var i = 0; i < 3; i++) {
        if (
          this.localMonths[i].documents.length ||
          this.localMonths[i].fileInput.length
        ) {
          month[i] = true;
        } else {
          if (
            this.localMonths[i].textareaData.length &&
            this.localMonths[i].checkboxValue
          ) {
            month[i] = true;
          } else {
            month[i] = false;
          }
        }
      }

      if (month[0] && month[1] && month[2]) {
        return true;
      } else {
        return false;
      }
    },
    localFilled: function () {
      if (this.localTypeMonths) {
        if (this.isMonthsFilled) {
          return true;
        } else {
          return false;
        }
      } else if (this.localTypeAlternate) {
        if (
          (this.localTextareaData != "" && this.checkboxState) ||
          this.isAlternateFormFilled ||
          this.isDocumentAttached
        ) {
          return true;
        } else {
          return false;
        }
      } else {
        if (
          (this.localTextareaData != "" && this.checkboxState) ||
          this.isDocumentAttached
        ) {
          return true;
        } else {
          return false;
        }
      }
    },
    localTypeMonths: function () {
      return this.type === "months";
    },
    localTypeDefault: function () {
      return this.type === "default";
    },
    localTypeAlternate: function () {
      return this.type === "alternate";
    },
    showUploadRail: function () {
      // Variant A+: accepted cards hide file input; show only "+ Extra document" button
      if (!this.uploadUrl) return true; // traditional mode: always show
      if (this.localAccepted && !this.extraSlotOpen) return false;
      return true;
    },
    showMonthUploadRail: function () {
      // Returns function(monthIndex) for months cards
      var that = this;
      return function (monthIndex) {
        if (!that.uploadUrl) return true;
        if (that.localMonths[monthIndex] && that.localMonths[monthIndex].documents.length && !that.monthsExtraSlotOpen[monthIndex]) return false;
        return true;
      };
    },
    // Accepted = real files per required month (not reason-only). Matches Blade SSR.
    isMonthsAccepted: function () {
      if (!this.localTypeMonths) return false;
      for (var i = 0; i < this.localMonths.length; i++) {
        if (!this.localMonths[i].documents.length) return false;
      }
      return true;
    },
    numberOfMonthsWithReason: function () {
      if (!this.localTypeMonths) return 0;
      var count = 0;
      for (var i = 0; i < this.localMonths.length; i++) {
        if (this.localMonths[i].checkboxValue) count++;
      }
      return count;
    },
    reasonIsDirty: function () {
      if (this.localTypeMonths) {
        for (var i = 0; i < this.localMonths.length; i++) {
          if (!!this.localMonths[i].checkboxValue !== (this.initialMonthCheckboxes[i] || false)) return true;
          if ((this.localMonths[i].textareaData || '').trim() !== (this.initialMonthReasons[i] || '')) return true;
        }
        return false;
      }
      if (this.localTypeAlternate) {
        return (this.localTextareaData || '').trim() !== (this.initialReason || '').trim()
          || !!this.checkboxState !== !!this.checkboxValue;
      }
      return (this.localTextareaData || '').trim() !== (this.initialReason || '').trim()
        || !!this.checkboxState !== !!this.checkboxValue;
    },
    // P1: high-emphasis only when reason content is "complete"
    reasonIsComplete: function () {
      if (this.localTypeMonths) {
        // All checked months must have non-empty reason text
        for (var i = 0; i < this.localMonths.length; i++) {
          if (this.localMonths[i].checkboxValue && !(this.localMonths[i].textareaData || '').trim()) return false;
        }
        return this.numberOfMonthsWithReason > 0;
      }
      // Default + alternate: checkbox checked AND non-empty textarea
      return this.checkboxState && (this.localTextareaData || '').trim().length > 0;
    },
    // Non-'ready' queue entries for a card slot (-1 = default/alternate card, N = month N) - the
    // template renders one progress/status row per entry instead of one scalar upload state, so
    // several files (this card, or several cards at once) can be in flight together.
    uploadsForSlot: function () {
      var that = this;

      return function (monthIndex) {
        return that.uploadQueue.filter(function (q) {
          return q.monthIndex === monthIndex && q.status !== 'ready';
        });
      };
    },
  },
  props: {
    id: {
      default: "",
      type: String,
    },
    tenantId: {
      default: "",
      type: String,
    },
    title: {
      default: "",
      type: String,
    },
    accepted: {
      default: false,
      type: Boolean,
    },
    notRequired: {
      default: false,
      type: Boolean,
    },
    subtitle: {
      default: "",
      type: String,
    },
    notification: {
      default: "",
      type: String,
    },
    notificationPrefix: {
      default: "",
      type: String,
    },
    buttonValue: {
      default: "",
      type: String,
    },
    addedDocuments: {
      default: () => [],
      type: Array,
    },
    checkboxText: {
      default: "",
      type: String,
    },
    checkboxValue: {
      default: false,
      type: Boolean,
    },
    textareaPlaceholder: {
      default: "",
      type: String,
    },
    textareaData: {
      default: "",
      type: String,
    },
    type: {
      default: "",
      type: String,
    },
    months: {
      default: () => [],
      type: Array,
    },
    smallFormDescription: {
      default: "",
      type: String,
    },
    smallForm: {
      default: () => [],
      type: Array,
    },
    cardFilledNotification: {
      default: "",
      type: String,
    },
    showReason: {
      default: true,
      type: Boolean,
    },
    edrAction: {
      default: "",
      type: String,
    },
    showEdrButton: {
      default: false,
      type: Boolean,
    },
    guaranteeAction: {
      default: "",
      type: String,
    },
    showGuaranteeButton: {
      default: false,
      type: Boolean,
    },
    surveyAction: {
      default: "",
      type: String,
    },
    showSurveyButton: {
      default: false,
      type: Boolean,
    },
    disableRemove: {
      default: false,
      type: Boolean,
    },
    // A frozen dossier: the documents stay listed, nothing is offered that the server refuses.
    readOnly: {
      default: false,
      type: Boolean,
    },
    // Set only for a document the candidate may never delete (the EDR check): its delete button
    // shows this message instead of calling /attachments/delete/{id}, which would refuse it anyway.
    removeBlockedMessage: {
      default: '',
      type: String,
    },
    surveyDisabled: {
      default: false,
      type: Boolean,
    },
    uploadUrl: {
      default: '',
      type: String,
    },
    reasonUrl: {
      default: '',
      type: String,
    },
    acceptedViaLandlord: {
      default: false,
      type: Boolean,
    },
    landlordUrl: {
      default: '',
      type: String,
    },
  },
  methods: {
    addInputTypeFile: function (index) {
      if (this.localTypeMonths) {
        this.monthsInputsTypeFile[index].push(1);
      } else {
        this.inputsTypeFile.push(1);
      }
    },
    deleteAddedDocument: function (id, index, index2) {
      if (this.removeBlockedMessage) {
        window.app.sweetalert(
          window.app.locales.common.error,
          this.removeBlockedMessage,
          "error"
        );
        return;
      }
      window.app
        .sweetalert(
          this.locales &&
            this.locales.users &&
            this.locales.users.are_you_sure_you_want_to_delete_this_document,
          {
            buttons: ["Cancel", "Ok"],
          }
        )
        .then((value) => {
          if (value) {
            var that = this;
            $.ajax({
              url: `/attachments/delete/` + id,
              type: "DELETE",
              success: function (response) {
                if (that.localTypeMonths) {
                  that.localMonths[index].documents.splice(index2, 1);
                } else {
                  that.alreadyAddedDocuments.splice(index, 1);
                }
                that.recomputeAccepted();
                window.app.sweetalert(
                  window.app.locales.common.success,
                  window.app.locales.attachments.documents_successfully_removed,
                  "success"
                ).then(function () {
                  // The page's finalization/"successfully submitted" state (and the delete
                  // endpoint's own RAC-reopen/documents_ready reset) is server-rendered and
                  // cannot be reconciled from this single card's local state - reload to pick up
                  // the authoritative state, matching the bulk-delete flow's own behaviour.
                  window.location.reload();
                });
              },
              error: function (response) {
                window.app.sweetalert(
                  window.app.locales.common.error,
                  window.app.locales.common.error_deleting,
                  "error"
                );
              },
            });
          }
        });
    },
    cardChanged: function () {
      // A read-only card shows its state as it is; a key in the readable reason changes nothing.
      if (this.readOnly) {
        return;
      }
      this.localAccepted = false;
      this.isDirty = true;
    },
    fileInputChanged: function (event, index) {
      this.isDirty = true;
      var parent = event.target.closest('.js-inputs-parent');
      var childList = parent.getElementsByClassName("document-card__input-file");
      if (this.localTypeMonths) {
        for (var i = 0; i < childList.length; i++) {
          if (childList[i].value != "") {
            this.localMonths[index].fileInput = "filled";
            break;
          } else {
            this.localMonths[index].fileInput = "";
          }
        }
      } else {
        for (var i = 0; i < childList.length; i++) {
          if (childList[i].value != "") {
            this.isDocumentAttached = true;
            break;
          } else {
            this.isDocumentAttached = false;
          }
        }
      }

      // Duplicate filename warning (all card types)
      if (event.target.files && event.target.files.length) {
        var selectedName = event.target.files[0].name;
        var existingDocs;
        if (this.localTypeMonths) {
          existingDocs = (this.localMonths[index] && this.localMonths[index].documents) || [];
        } else {
          existingDocs = this.alreadyAddedDocuments || [];
        }
        var isDuplicate = existingDocs.some(function(doc) { return doc.imageName === selectedName; });
        // Also check against other pending file inputs in the same card
        if (!isDuplicate) {
          for (var k = 0; k < childList.length; k++) {
            if (childList[k] !== event.target && childList[k].files && childList[k].files.length && childList[k].files[0].name === selectedName) {
              isDuplicate = true;
              break;
            }
          }
        }
        if (isDuplicate) {
          var that = this;
          var input = event.target;
          var warningText = window.app.locales.documents && window.app.locales.documents.duplicate_file_warning || 'documents.duplicate_file_warning';
          var addAnywayText = window.app.locales.documents && window.app.locales.documents.add_anyway || 'documents.add_anyway';
          var cancelText = window.app.locales.common && window.app.locales.common.cancel || 'common.cancel';
          window.app.sweetalert(warningText, {
            buttons: [cancelText, addAnywayText],
            dangerMode: false
          }).then(function(value) {
            if (!value) {
              input.value = '';
              // Recompute isDocumentAttached from remaining file inputs
              var remaining = parent.getElementsByClassName("document-card__input-file");
              var hasFile = false;
              for (var j = 0; j < remaining.length; j++) {
                if (remaining[j].value != "") { hasFile = true; break; }
              }
              that.isDocumentAttached = hasFile;
            }
          });
        }
      }
    },
    requestEdrLink: function (e) {
      e.preventDefault();
      if (this.edrRequestInFlight) {
        return;
      }
      var action = e.target.getAttribute("formaction");
      this.edrRequestInFlight = true;
      document.querySelector(".js-loader-item").style.display = "";
      var that = this;
      $.ajax({
        url: action,
        method: "post",
        success: function (result) {
          document.querySelector(".js-loader-item").style.display = "none";
          if (result && result.redirect) {
            // Payment creation happens later, only once the tenant presses the payment
            // button on the request-edr page itself - this just gets them there.
            window.location.assign(result.redirect);
          } else {
            that.edrRequestInFlight = false;
            window.app.sweetalert(
              window.app.locales.common.error,
              window.app.locales.common.error,
              "error"
            );
          }
        },
        error: function (result) {
          document.querySelector(".js-loader-item").style.display = "none";
          that.edrRequestInFlight = false;
          // The specific edr.missing_postcode/house_number/birth_date messages (and their
          // redirect) are a distinct validation workflow from document-upload errors and must
          // keep reaching the candidate exactly as the backend phrased them - normalizeUploadError
          // passes a structured body's own message/error straight through unchanged.
          var normalized = that.normalizeUploadError({ status: result.status, responseJSON: result.responseJSON, responseText: result.responseText });
          window.app
            .sweetalert(window.app.locales.common.error, that.formatUploadErrorMessage(normalized), "error")
            .then((value) => {
              if (normalized.redirect) {
                window.location.assign(normalized.redirect);
              }
            });
        },
      });
    },
    requestGuarantee: function (e) {
      e.preventDefault();
      var action = e.target.getAttribute("formaction");
      // console.log(action);
      document.querySelector(".js-loader-item").style.display = "";
      var that = this;
      var message = window.app.locales.documents.guarantee_sent;
      $.ajax({
        url: action,
        method: "post",
        success: function (result) {
          document.querySelector(".js-loader-item").style.display = "none";
          window.app.sweetalert(
            window.app.locales.common.success,
            message,
            "success"
          );
        },
        error: function (result) {
          document.querySelector(".js-loader-item").style.display = "none";
          var normalized = that.normalizeUploadError({ status: result.status, responseJSON: result.responseJSON, responseText: result.responseText });
          window.app
            .sweetalert(window.app.locales.common.error, that.formatUploadErrorMessage(normalized), "error")
            .then((value) => {
              if (normalized.redirect) {
                window.location.assign(normalized.redirect);
              }
            });
        },
      });
    },
    sendSurvey: function (e) {
      e.preventDefault();
      var action = e.target.getAttribute("formaction");
      var that = this;
      that.disabled = true;

      // Client-side email format check
      if (this.localSmallForm && this.localSmallForm.length >= 3) {
        var emailVal = (this.localSmallForm[2].inputData || '').trim();
        if (emailVal && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailVal)) {
          window.app.sweetalert(window.app.locales.common.error,
            window.app.locales.validation && window.app.locales.validation.email || 'Invalid email', 'error');
          that.disabled = false;
          return;
        }
      }

      // Step 1: Persist landlord fields if dirty and landlordUrl available
      var landlordDirty = false;
      var landlordPayload = {};
      if (this.landlordUrl && this.localSmallForm && this.localSmallForm.length >= 3) {
        landlordPayload = {
          tenant_id: this.tenantId,
          landlord_name: (this.localSmallForm[0].inputData || '').trim(),
          landlord_phone_number: (this.localSmallForm[1].inputData || '').trim(),
          landlord_email: (this.localSmallForm[2].inputData || '').trim()
        };
        // Check if any field has content (always persist to be safe)
        landlordDirty = !!(landlordPayload.landlord_name || landlordPayload.landlord_phone_number || landlordPayload.landlord_email);
      }

      var doSurvey = function() {
        document.querySelector(".js-loader-item").style.display = "";
        var message = window.app.locales.documents.survey_sent;
        $.ajax({
          url: action,
          method: "post",
          success: function (result) {
            document.querySelector(".js-loader-item").style.display = "none";
            that.localAccepted = true;
            that.disabled = false;
            var successMessage = (result && result.message) || message || window.app.locales.common.success;
            window.app.sweetalert(window.app.locales.common.success, successMessage, "success");
          },
          error: function (result) {
            document.querySelector(".js-loader-item").style.display = "none";
            var normalized = that.normalizeUploadError({ status: result.status, responseJSON: result.responseJSON, responseText: result.responseText });
            window.app.sweetalert(window.app.locales.common.error, that.formatUploadErrorMessage(normalized), "error")
              .then(function() { that.disabled = false; });
          },
        });
      };

      if (landlordDirty && this.landlordUrl) {
        // Persist landlord first, then survey
        var token = document.querySelector('meta[name="csrf-token"]');
        var xhr = new XMLHttpRequest();
        xhr.open('POST', this.landlordUrl, true);
        xhr.setRequestHeader('Content-Type', 'application/json');
        xhr.setRequestHeader('X-Requested-With', 'XMLHttpRequest');
        if (token) xhr.setRequestHeader('X-CSRF-TOKEN', token.getAttribute('content'));
        xhr.onload = function() {
          if (xhr.status === 200) {
            doSurvey();
          } else if (xhr.status === 419) {
            var msg = (window.app.locales.common && window.app.locales.common.session_expired_will_reload) || 'Session expired.';
            window.app.sweetalert(window.app.locales.common.error, msg, 'error').then(function() { window.location.reload(); });
          } else {
            var errMsg = window.app.locales.common.error;
            try { var resp = JSON.parse(xhr.responseText); if (resp.error) errMsg = resp.error; } catch(ex) {}
            window.app.sweetalert(window.app.locales.common.error, errMsg, 'error')
              .then(function() { that.disabled = false; });
          }
        };
        xhr.onerror = function() {
          window.app.sweetalert(window.app.locales.common.error, 'Network error', 'error')
            .then(function() { that.disabled = false; });
        };
        xhr.send(JSON.stringify(landlordPayload));
      } else {
        doSurvey();
      }
    },
    emitChange: function(value) {
      this.$emit('change', value);
    },
    alternateCheckboxChanged: function() {
      this.isDirty = true;
      if (!this.checkboxState) {
        // Unchecked: clear reason state, re-show upload rail
        this.localTextareaData = '';
        this.extraSlotOpen = false;
        this.uploadKeyCounter++;
        this.recomputeAccepted();
      } else {
        this.localAccepted = false;
      }
    },
    recomputeAccepted: function() {
      // Central helper: single source of truth for localAccepted updates
      if (this.localTypeMonths) {
        this.localAccepted = this.isMonthsAccepted;
      } else {
        // Default + alternate: files present, or landlord form filled (alternate only)
        this.localAccepted = !!this.alreadyAddedDocuments.length || this.acceptedViaLandlord;
      }
    },
    openExtraUploadSlot: function() {
      this.extraSlotOpen = true;
      this.uploadKeyCounter++;
    },
    openMonthExtraUploadSlot: function(monthIndex) {
      this.$set(this.monthsExtraSlotOpen, monthIndex, true);
      this.uploadKeyCounter++;
    },
    /**
     * Single source of truth for turning a failed XHR (upload, reason save, EDR request,
     * guarantee request, survey) into a candidate-facing message. Works for both transports used
     * in this component: pass {status, responseText} from a raw XMLHttpRequest's onerror/onload,
     * or {status, responseJSON, responseText} from a jQuery jqXHR error callback. {isTimeout:true}
     * / {isAbort:true} distinguish those from a plain network failure.
     *
     * @return {message, code, retryable, requestId, redirect}
     */
    normalizeUploadError: function (input) {
      var locales = (window.app && window.app.locales) || {};
      var common = locales.common || {};
      var documents = locales.documents || {};
      input = input || {};

      if (input.isAbort) {
        return { message: common.upload_interrupted || 'The upload connection was interrupted. Check your connection and try again.', code: 'aborted', retryable: true, requestId: null };
      }
      if (input.isTimeout) {
        return { message: documents.upload_error_pdf_processing_timeout || 'The server took too long to process this document. Please try again with a smaller PDF.', code: 'timeout', retryable: true, requestId: null };
      }

      // A structured body is authoritative whenever the caller has one, independent of whether a
      // numeric HTTP status was also supplied - the EDR/upload endpoints' own {error, redirect}
      // (and jQuery callers that only pass responseJSON, no status) must always take this path.
      var body = input.responseJSON;
      if (!body && input.responseText) {
        try {
          body = JSON.parse(input.responseText);
        } catch (e) {
          body = null; // HTML or otherwise non-JSON body - fall through below.
        }
      }

      var requestId = (body && body.request_id) || null;

      if (body && (body.message || body.error)) {
        return {
          message: body.message || body.error,
          code: body.code || (input.status ? String(input.status) : 'error'),
          retryable: !!body.retryable,
          requestId: requestId,
          redirect: body.redirect,
        };
      }

      // A 403 with a Ray ID but none of our JSON: Cloudflare's WAF answered and the request never
      // reached Laravel. Not a permissions problem, and resending the same bytes is blocked again.
      if (input.status === 403 && input.cfRay) {
        return { message: documents.upload_error_edge_blocked || 'This file was blocked by our security filter before it reached our server. Please contact us and mention this reference.', code: 'edge_blocked', retryable: false, requestId: input.cfRay };
      }

      if (!input.status) {
        // status 0: no HTTP response reached the browser at all, and no structured body either -
        // offline and "connection reset mid-request" both look identical to XHR and can only be
        // told apart via navigator.onLine.
        if (typeof navigator !== 'undefined' && navigator.onLine === false) {
          return { message: common.upload_offline || 'You appear to be offline. Check your connection and try again.', code: 'offline', retryable: true, requestId: null };
        }
        return { message: common.upload_interrupted || 'The upload connection was interrupted. Check your connection and try again.', code: 'network_error', retryable: true, requestId: null };
      }

      switch (input.status) {
        case 401:
          return { message: common.session_expired_will_reload || 'Your session has expired. The page will reload; please select the file again.', code: 'unauthenticated', retryable: false, requestId: requestId };
        case 403:
          return { message: documents.upload_error_forbidden || 'You do not have permission to upload documents for this application.', code: 'forbidden', retryable: false, requestId: requestId };
        case 404:
          return { message: documents.upload_error_subscription_not_found || 'This application could not be found. Please refresh the page and try again.', code: 'not_found', retryable: false, requestId: requestId };
        case 413:
          return { message: documents.upload_error_file_too_large || 'This file is larger than the maximum allowed size of 20 MB.', code: 'file_too_large', retryable: false, requestId: requestId };
        case 419:
          return { message: common.session_expired_will_reload || 'Your session has expired. The page will reload; please select the file again.', code: 'session_expired', retryable: false, requestId: requestId };
        case 422:
          return { message: documents.upload_error_unsupported_mime || 'This file type is not supported. Upload a PDF, JPG or PNG.', code: 'validation_failed', retryable: false, requestId: requestId };
        case 429:
          return { message: common.too_many_attempts || 'Too many attempts. Please wait a moment and try again.', code: 'rate_limited', retryable: true, requestId: requestId };
        case 500:
          return { message: documents.upload_error_pdf_processing_failed || 'This PDF could not be processed. It may be password-protected or damaged. Save an unprotected copy and try again.', code: 'processing_failed', retryable: true, requestId: requestId };
        case 502:
        case 503:
        case 504:
          return { message: common.upload_server_temporary || 'The server is temporarily unavailable. Please try again shortly.', code: 'server_temporary', retryable: true, requestId: requestId };
        default:
          var template = common.upload_unknown_with_reference || 'We could not upload this document. Reference: :request_id.';
          return { message: template.replace(':request_id', requestId || 'n/a'), code: 'unknown', retryable: false, requestId: requestId };
      }
    },
    /** Appends the request id (when present) so a candidate can quote it back to support. */
    formatUploadErrorMessage: function (normalized) {
      if (!normalized.requestId) {
        return normalized.message;
      }
      return normalized.message + ' (' + normalized.requestId + ')';
    },
    /**
     * Resumable, chunked upload pipeline. Replaces the old single-request _doUpload(): a file of
     * any size now transports as many small PUT requests (server-declared chunk_size, ~5-8 MiB)
     * instead of depending on one long-lived HTTP request surviving Cloudflare/nginx/browser
     * timeouts for the full qpdf/Ghostscript/pdftk/mPDF pipeline. Processing itself happens
     * entirely server-side, off this request cycle - see finalizeUpload()/pollUploadStatus().
     */
    _uploadsBaseUrl: function () {
      // The two existing Blade views only ever pass uploadUrl = route('account.tenants.upload-
      // single', $subscription->id); deriving the chunked base from it keeps this change out of
      // those views entirely.
      return (this.uploadUrl || '').replace(/upload-single\/?$/, 'uploads');
    },
    _csrfToken: function () {
      var token = document.querySelector('meta[name="csrf-token"]');

      return token ? token.getAttribute('content') : null;
    },
    _xhrJson: function (method, url, body, timeoutMs) {
      var that = this;

      return new Promise(function (resolve, reject) {
        var xhr = new XMLHttpRequest();
        xhr.open(method, url, true);
        xhr.setRequestHeader('X-Requested-With', 'XMLHttpRequest');
        xhr.setRequestHeader('Content-Type', 'application/json');
        var token = that._csrfToken();
        if (token) xhr.setRequestHeader('X-CSRF-TOKEN', token);
        xhr.timeout = timeoutMs || 30000;
        xhr.onload = function () {
          var response = null;
          try { response = xhr.responseText ? JSON.parse(xhr.responseText) : null; } catch (e) { /* non-JSON body handled below */ }
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve(response || {});
          } else {
            reject(that.normalizeUploadError({ status: xhr.status, responseJSON: response, responseText: xhr.responseText, cfRay: that._cfRay(xhr) }));
          }
        };
        xhr.ontimeout = function () { reject(that.normalizeUploadError({ isTimeout: true })); };
        xhr.onerror = function () { reject(that.normalizeUploadError({ status: xhr.status })); };
        xhr.send(body ? JSON.stringify(body) : null);
      });
    },
    /** Raw-binary PUT - a chunk's body must never be JSON-wrapped. */
    _xhrChunk: function (url, blob, checksum, onProgress, timeoutMs) {
      var that = this;

      return new Promise(function (resolve, reject) {
        var xhr = new XMLHttpRequest();
        xhr.open('PUT', url, true);
        xhr.setRequestHeader('X-Requested-With', 'XMLHttpRequest');
        xhr.setRequestHeader('Content-Type', 'application/octet-stream');
        xhr.setRequestHeader('X-Chunk-Checksum', checksum || '');
        var token = that._csrfToken();
        if (token) xhr.setRequestHeader('X-CSRF-TOKEN', token);
        xhr.timeout = timeoutMs || 60000;
        xhr.upload.onprogress = function (e) {
          if (e.lengthComputable && onProgress) onProgress(e.loaded, e.total);
        };
        xhr.onload = function () {
          var response = null;
          try { response = xhr.responseText ? JSON.parse(xhr.responseText) : null; } catch (e) { /* non-JSON body handled below */ }
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve(response || {});
          } else {
            reject(that.normalizeUploadError({ status: xhr.status, responseJSON: response, responseText: xhr.responseText, cfRay: that._cfRay(xhr) }));
          }
        };
        xhr.ontimeout = function () { reject(that.normalizeUploadError({ isTimeout: true })); };
        xhr.onerror = function () { reject(that.normalizeUploadError({ status: xhr.status })); };
        xhr.send(blob);
      });
    },
    /** Cloudflare's Ray ID for this response, when it went through Cloudflare - the reference its Security Events are searched by. */
    _cfRay: function (xhr) {
      return (typeof xhr.getResponseHeader === 'function' && xhr.getResponseHeader('cf-ray')) || null;
    },
    /** SHA-256 of a Blob via SubtleCrypto (secure contexts only). Empty string when unavailable - the server's declared-vs-actual size check still guards correctness. */
    _sha256Hex: function (blob) {
      if (!window.crypto || !window.crypto.subtle || typeof blob.arrayBuffer !== 'function') {
        return Promise.resolve('');
      }

      return blob.arrayBuffer().then(function (buffer) {
        return window.crypto.subtle.digest('SHA-256', buffer).then(function (hash) {
          var bytes = new Uint8Array(hash);
          var hex = '';
          for (var i = 0; i < bytes.length; i++) {
            hex += ('0' + bytes[i].toString(16)).slice(-2);
          }

          return hex;
        });
      }).catch(function () {
        return '';
      });
    },
    /** Bounded global concurrency across every card/upload on the page, conservative default. */
    processUploadQueue: function () {
      window.app.candidateUploadInFlight = window.app.candidateUploadInFlight || 0;
      var concurrency = (window.app && window.app.candidateUploadConcurrency) || 3;
      var pending = this.uploadQueue.filter(function (q) { return q.status === 'pending'; });
      for (var i = 0; i < pending.length && window.app.candidateUploadInFlight < concurrency; i++) {
        window.app.candidateUploadInFlight++;
        this.runUpload(pending[i]);
      }
    },
    runUpload: function (item) {
      item.status = 'uploading';
      var that = this;
      var releaseSlot = function () {
        window.app.candidateUploadInFlight = Math.max(0, (window.app.candidateUploadInFlight || 1) - 1);
        that.processUploadQueue();
      };
      this.initUploadSession(item)
        .then(function () { return that.uploadChunksInOrder(item); })
        .then(function () { return that.finalizeUpload(item); })
        .then(function () {
          that.pollUploadStatus(item);
          releaseSlot();
        })
        .catch(function (err) {
          item.status = 'failed';
          item.error = that.formatUploadErrorMessage(err || {});
          item.retryable = !!(err && err.retryable);
          releaseSlot();
        });
    },
    initUploadSession: function (item) {
      var that = this;

      return this._xhrJson('POST', this._uploadsBaseUrl(), {
        tenant_id: item.tenantId,
        type_id: item.typeId,
        month_year: item.monthYear,
        expected_size: item.file.size,
        original_filename: item.name,
        extension: this.uploadFileExtension(item.name),
      }).then(function (response) {
        item.uploadId = response.upload_id;
        item.chunkSize = response.chunk_size;
        item.chunkCount = response.chunk_count;
        that.persistResumeState(item);

        return response;
      });
    },
    /** Chunks are sent in order (their server-side write is idempotent, so a resumed/retried run can safely re-send an already-received index too). */
    uploadChunksInOrder: function (item) {
      var that = this;
      var chain = Promise.resolve();
      var attach = function (index) {
        chain = chain.then(function () { return that.uploadOneChunkWithBackoff(item, index); });
      };
      for (var i = 0; i < item.chunkCount; i++) {
        attach(i);
      }

      return chain;
    },
    uploadOneChunkWithBackoff: function (item, index, attempt) {
      attempt = attempt || 1;
      var that = this;
      var start = index * item.chunkSize;
      var blob = item.file.slice(start, start + item.chunkSize);

      return this._sha256Hex(blob).then(function (checksum) {
        var url = that._uploadsBaseUrl() + '/' + item.uploadId + '/chunks/' + index;

        return that._xhrChunk(url, blob, checksum, function (loaded) {
          var doneBytes = Math.min(start + loaded, item.file.size);
          item.progress = Math.round((doneBytes / item.file.size) * 100);
        }, 60000);
      }).then(function () {
        item.progress = Math.min(100, Math.round(((index + 1) * item.chunkSize / item.file.size) * 100));
      }).catch(function (err) {
        // Transient transport failures (network, timeout, a momentary 5xx) get bounded retries
        // with exponential backoff - a one-off checksum/size mismatch (also possible from a
        // corrupted-in-transit request) is retried the same way rather than failing the whole
        // file over what may be a single bad request.
        var maxAttempts = 5;
        // A recognised refusal (ours, or the edge's WAF block) answers every resend the same way. An
        // unrecognised status ('unknown': e.g. Cloudflare's transient 520-524) keeps the backoff.
        if (attempt >= maxAttempts || (err && err.retryable === false && err.code !== 'unknown')) {
          throw err;
        }
        var delay = Math.min(1000 * Math.pow(2, attempt - 1), 15000);

        return new Promise(function (resolve) { setTimeout(resolve, delay); })
          .then(function () { return that.uploadOneChunkWithBackoff(item, index, attempt + 1); });
      });
    },
    finalizeUpload: function (item) {
      return this._xhrJson('POST', this._uploadsBaseUrl() + '/' + item.uploadId + '/finalize', {}).then(function (response) {
        // 202: transport is complete, processing has only just been queued - never treated as
        // "ready" here. pollUploadStatus() is the only place that adds the finished document.
        item.status = response.status || 'queued';

        return response;
      });
    },
    /** Bounded-backoff status polling until the job reaches 'ready' or 'failed'. */
    pollUploadStatus: function (item, attempt) {
      attempt = attempt || 1;
      var that = this;
      var delay = Math.min(2000 * Math.pow(1.5, attempt - 1), 15000);
      item._pollTimer = setTimeout(function () {
        that._xhrJson('GET', that._uploadsBaseUrl() + '/' + item.uploadId, null, 15000).then(function (response) {
          item.status = response.status;
          if (response.status === 'ready') {
            that.onUploadReady(item, response);

            return;
          }
          if (response.status === 'failed') {
            var errorKey = response.error_code ? 'upload_error_' + response.error_code : null;
            item.error = (errorKey && window.app.locales.documents && window.app.locales.documents[errorKey])
              || (response.error_code === 'pdf_encrypted' && PDF_ENCRYPTED_MESSAGE)
              || (window.app.locales.common && window.app.locales.common.error) || 'Error';
            // The server decides (CandidateDocumentUpload::isRetryableErrorCode()): retrying a
            // password-protected PDF or an unsupported file would only fail the same way again.
            item.retryable = response.retryable === true;
            that.clearResumeState(item);

            return;
          }
          that.pollUploadStatus(item, attempt + 1);
        }).catch(function () {
          // A transient status-poll failure keeps polling - the document may already be safely
          // processing server-side even though this one status check failed to reach it.
          that.pollUploadStatus(item, attempt + 1);
        });
      }, delay);
    },
    onUploadReady: function (item, response) {
      var newDoc = response.attachment;
      if (item.monthIndex >= 0) {
        this.localMonths[item.monthIndex].documents.push(newDoc);
        this.localMonths[item.monthIndex].fileInput = 'filled';
        this.$set(this.monthsExtraSlotOpen, item.monthIndex, false);
      } else {
        this.alreadyAddedDocuments.push(newDoc);
        this.extraSlotOpen = false;
      }
      this.isDirty = false;
      this.uploadKeyCounter++;
      this.recomputeAccepted();
      this.clearResumeState(item);
      var that = this;
      // Brief "ready" flash before the row leaves the in-flight list (it is now part of
      // alreadyAddedDocuments/localMonths[].documents, rendered by the existing document list).
      setTimeout(function () {
        var idx = that.uploadQueue.indexOf(item);
        if (idx !== -1) that.uploadQueue.splice(idx, 1);
      }, 1200);
    },
    retryUpload: function (item) {
      if (!item.retryable) return;
      if (item._pollTimer) { clearTimeout(item._pollTimer); }
      item.status = 'pending';
      item.error = '';
      item.progress = 0;
      this.processUploadQueue();
    },
    _resumeStorageKey: function () {
      return 'candidateUploadResume:' + this.tenantId + ':' + this.id;
    },
    /** Best-effort only: sessionStorage can be unavailable (private browsing) or full - a failure here never blocks the upload itself. */
    persistResumeState: function (item) {
      try {
        var key = this._resumeStorageKey();
        var stored = JSON.parse(sessionStorage.getItem(key) || '[]');
        stored = stored.filter(function (s) { return s.uploadId !== item.uploadId; });
        stored.push({ uploadId: item.uploadId, name: item.name, monthIndex: item.monthIndex, monthYear: item.monthYear });
        sessionStorage.setItem(key, JSON.stringify(stored));
      } catch (e) { /* best-effort */ }
    },
    clearResumeState: function (item) {
      try {
        var key = this._resumeStorageKey();
        var stored = JSON.parse(sessionStorage.getItem(key) || '[]');
        stored = stored.filter(function (s) { return s.uploadId !== item.uploadId; });
        sessionStorage.setItem(key, JSON.stringify(stored));
      } catch (e) { /* best-effort */ }
    },
    /**
     * Restores in-flight uploads after a page refresh/navigation instead of losing them: any
     * session id persisted by persistResumeState() gets one immediate status poll, which
     * reconciles to whatever the server authoritatively reports (queued/processing/ready/failed)
     * rather than assuming anything about what happened while the tab was closed.
     */
    restorePendingUploads: function () {
      if (!this.uploadUrl) return;
      try {
        var key = this._resumeStorageKey();
        var stored = JSON.parse(sessionStorage.getItem(key) || '[]');
        var that = this;
        stored.forEach(function (s) {
          var item = {
            id: 'resume-' + s.uploadId, file: null, name: s.name, size: 0,
            typeId: that.id, tenantId: that.tenantId, monthYear: s.monthYear,
            monthIndex: typeof s.monthIndex === 'number' ? s.monthIndex : -1,
            status: 'processing', progress: 100, error: '', retryable: false,
            uploadId: s.uploadId, attachment: null,
          };
          that.uploadQueue.push(item);
          that.pollUploadStatus(item, 1);
        });
      } catch (e) { /* best-effort */ }
    },
    _saveReason: function(e) {
      if (e) e.preventDefault();
      if (!this.reasonUrl || this.loading) return;
      var that = this;
      that.loading = true;

      var token = document.querySelector('meta[name="csrf-token"]');
      var headers = { 'X-Requested-With': 'XMLHttpRequest', 'Content-Type': 'application/json' };
      if (token) headers['X-CSRF-TOKEN'] = token.getAttribute('content');

      // Collect all reasons to save
      var saves = [];
      if (this.localTypeMonths) {
        for (var i = 0; i < this.localMonths.length; i++) {
          var m = this.localMonths[i];
          if (m.checkboxValue) {
            saves.push({ tenant_id: this.tenantId, type_id: this.id, reason: m.textareaData || '', month_year: m.index });
          } else if (this.initialMonthCheckboxes[i]) {
            // Checkbox was unchecked — send empty reason to delete
            saves.push({ tenant_id: this.tenantId, type_id: this.id, reason: '', month_year: m.index });
          }
        }
      } else {
        if (this.checkboxState) {
          saves.push({ tenant_id: this.tenantId, type_id: this.id, reason: this.localTextareaData || '' });
        } else if (this.checkboxValue) {
          // Checkbox was unchecked — send empty to delete
          saves.push({ tenant_id: this.tenantId, type_id: this.id, reason: '' });
        }
      }

      if (!saves.length) { that.loading = false; return; }

      var completed = 0;
      var hasError = false;
      var lastErrorResult = null;
      var finishIfDone = function() {
        if (completed !== saves.length) return;
        that.loading = false;
        if (!hasError) {
          // Resync snapshots — current state is now the baseline
          if (that.localTypeMonths) {
            that.initialMonthReasons = that.localMonths.map(function(m) { return (m.textareaData || '').trim(); });
            that.initialMonthCheckboxes = that.localMonths.map(function(m) { return !!m.checkboxValue; });
          } else {
            that.initialReason = (that.localTextareaData || '').trim();
          }
          that.isDirty = false;
          // Brief success feedback
          var successMsg = (window.app.locales.common && window.app.locales.common.saved) || 'Opgeslagen';
          window.app.sweetalert(window.app.locales.common.success, successMsg, 'success');
        } else {
          window.app.sweetalert(window.app.locales.common.error, that.formatUploadErrorMessage(lastErrorResult), 'error');
        }
      };
      saves.forEach(function(payload) {
        var xhr = new XMLHttpRequest();
        xhr.open('POST', that.reasonUrl, true);
        Object.keys(headers).forEach(function(k) { xhr.setRequestHeader(k, headers[k]); });
        xhr.timeout = 30000;
        xhr.onload = function() {
          completed++;
          if (xhr.status === 200) {
            finishIfDone();
            return;
          }
          hasError = true;
          lastErrorResult = that.normalizeUploadError({ status: xhr.status, responseText: xhr.responseText });
          if (lastErrorResult.code === 'session_expired' || lastErrorResult.code === 'unauthenticated') {
            that.loading = false;
            window.app.sweetalert(window.app.locales.common.error, lastErrorResult.message, 'error').then(function() {
              window.location.reload();
            });
            return;
          }
          finishIfDone();
        };
        xhr.ontimeout = function() {
          completed++;
          hasError = true;
          lastErrorResult = that.normalizeUploadError({ isTimeout: true });
          finishIfDone();
        };
        xhr.onerror = function() {
          completed++;
          hasError = true;
          lastErrorResult = that.normalizeUploadError({ status: xhr.status });
          finishIfDone();
        };
        xhr.send(JSON.stringify(payload));
      });
    },
    /** Lower-cased extension after the last dot, or '' when the name has none. */
    uploadFileExtension: function (name) {
      var dot = (name || '').lastIndexOf('.');

      return dot === -1 ? '' : name.slice(dot + 1).toLowerCase();
    },
    isSupportedUploadFile: function (file) {
      return SUPPORTED_UPLOAD_EXTENSIONS.indexOf(this.uploadFileExtension(file.name)) !== -1;
    },
    /**
     * Multi-file selection (event.target.files, not just files[0]) - no product-level count
     * limit and no client-side file-size cap: config('candidate_uploads.max_file_size') is the
     * only place a completed-file size ceiling can exist, and it is disabled by default.
     */
    handleUploadFileChange: function(event, monthIndex) {
      if (!this.uploadUrl) return;
      var files = event.target.files;
      if (!files || !files.length) return;

      var monthYear = null;
      if (typeof monthIndex === 'number' && monthIndex >= 0 && this.localMonths[monthIndex]) {
        monthYear = this.localMonths[monthIndex].index;
      }
      var resolvedMonthIndex = typeof monthIndex === 'number' ? monthIndex : -1;

      for (var i = 0; i < files.length; i++) {
        var file = files[i];
        var baseItem = {
          id: 'u-' + Date.now() + '-' + i + '-' + Math.random().toString(36).slice(2),
          file: file, name: file.name, size: file.size,
          typeId: this.id, tenantId: this.tenantId, monthYear: monthYear,
          monthIndex: resolvedMonthIndex,
          progress: 0, error: '', retryable: false, uploadId: null, attachment: null,
        };
        if (!this.isSupportedUploadFile(file)) {
          baseItem.status = 'failed';
          baseItem.error = (window.app.locales.documents && window.app.locales.documents.upload_error_unsupported_mime)
            || 'This file type is not supported. Upload a PDF, JPG or PNG.';
        } else {
          baseItem.status = 'pending';
        }
        this.uploadQueue.push(baseItem);
      }

      this.processUploadQueue();
    },
    toggleAllFiles: function() {
      const root = this.$el;
      const initialInput = root.querySelector('input[name="check_all"][type="checkbox"]');
      const inputs = root.querySelectorAll('input[name="remove[]"][form="removeForm"][type="checkbox"]');

      if (inputs.length) {
        // Default cards: toggle existing per-file checkboxes
        inputs.forEach(input => {
          if (initialInput.checked) {
            if(!input.checked) {
              input.click();
            }
          } else {
            if(input.checked) {
              input.click();
            }
          }
        });
      } else if (this.alreadyAddedDocuments && this.alreadyAddedDocuments.length) {
        // Months/alternate cards: inject/remove hidden inputs for bulk delete
        var removeForm = document.getElementById('removeForm');
        if (!removeForm) return;
        var cardId = this.$el.id || ('card-' + this.tenantId + '-' + this.id);

        if (initialInput.checked) {
          this.alreadyAddedDocuments.forEach(function(doc) {
            var input = document.createElement('input');
            input.type = 'hidden';
            input.name = 'remove[]';
            input.value = doc.id;
            input.dataset.cardSource = cardId;
            removeForm.appendChild(input);
          });
          this.$emit('change', this.alreadyAddedDocuments.map(function(d) { return d.id; }));
        } else {
          var hiddens = removeForm.querySelectorAll('input[data-card-source="' + cardId + '"]');
          hiddens.forEach(function(el) { el.remove(); });
          this.$emit('change', '');
        }
      }
    }
  },
  mounted() {
    var that = this;
    this._resetLoadingHandler = function() { that.loading = false; that.isDirty = false; };
    this.$el.addEventListener('reset-loading', this._resetLoadingHandler);

    // Bulk delete sync: listen for cross-card removal event
    this._bulkRemovedHandler = function(e) {
      var ids = (e.detail && e.detail.ids) || [];
      if (!ids.length) return;
      // Remove from default/alternate documents
      that.alreadyAddedDocuments = that.alreadyAddedDocuments.filter(function(doc) {
        return ids.indexOf(String(doc.id)) === -1 && ids.indexOf(Number(doc.id)) === -1;
      });
      // Remove from month documents
      if (that.localTypeMonths) {
        for (var i = 0; i < that.localMonths.length; i++) {
          that.localMonths[i].documents = that.localMonths[i].documents.filter(function(doc) {
            return ids.indexOf(String(doc.id)) === -1 && ids.indexOf(Number(doc.id)) === -1;
          });
          // Reset fileInput if month now empty
          if (!that.localMonths[i].documents.length) {
            that.localMonths[i].fileInput = '';
          }
        }
      }
      that.recomputeAccepted();
      that.extraSlotOpen = false;
    };
    window.addEventListener('attachments-bulk-removed', this._bulkRemovedHandler);

    // Snapshot month reasons + checkboxes for dirty tracking
    if (this.localTypeMonths && this.localMonths) {
      this.initialMonthReasons = this.localMonths.map(function(m) { return (m.textareaData || '').trim(); });
      this.initialMonthCheckboxes = this.localMonths.map(function(m) { return !!m.checkboxValue; });
    }

    // Resume any upload sessions still in flight from before a refresh/navigation - see
    // restorePendingUploads()'s own docblock.
    this.restorePendingUploads();
  },
  beforeDestroy() {
    if (this._resetLoadingHandler) {
      this.$el.removeEventListener('reset-loading', this._resetLoadingHandler);
    }
    if (this._bulkRemovedHandler) {
      window.removeEventListener('attachments-bulk-removed', this._bulkRemovedHandler);
    }
    // Status polling must not keep firing (or touch this destroyed component's state) after
    // navigation away - the session itself is untouched server-side and resumes normally via
    // restorePendingUploads() on the next page that mounts this card.
    this.uploadQueue.forEach(function (upload) {
      if (upload._pollTimer) { clearTimeout(upload._pollTimer); }
    });
  },
  watch: {
    documentClasses: function () {
      var event = new CustomEvent("card-changed", {
        detail: "Triggered on card change",
      });
      setTimeout(function () {
        document.dispatchEvent(event);
      }, 0);
    },
    // P2: resync snapshots when props change without full page reload (SPA / partial refresh)
    textareaData: function (val) {
      this.initialReason = (val || '').trim();
      this.localTextareaData = val;
    },
    months: {
      deep: true,
      handler: function (val) {
        if (val && this.localTypeMonths) {
          this.localMonths = val;
          this.initialMonthReasons = val.map(function(m) { return (m.textareaData || '').trim(); });
          this.initialMonthCheckboxes = val.map(function(m) { return !!m.checkboxValue; });
        }
      },
    },
  },
};
</script>